import { getEuclideanDistance } from "utils/math/geometry/getEuclideanDistance";
import { initializeVertex } from "utils/math/geometry/initializeVertex";
import type { NodeMap } from "utils/math/geometry/findClosestPoint/types";
import type { Graph } from "utils/math/geometry/initializeVertex/types";

export type EdgeSplit = {
  virtualNodeId: string;
  splitPoint: { x: number; y: number };
};

export type EdgeSplitGroup = {
  nodeAId: string;
  nodeBId: string;
  splits: EdgeSplit[];
};

type EdgeSplitQueue = {
  edgeSplits: Map<string, EdgeSplitGroup>;
  getEdgeKey: (nodeAId: string, nodeBId: string) => string;
  queueEdgeSplit: (args: {
    nodeAId: string;
    nodeBId: string;
    splitPoint: { x: number; y: number };
    virtualNodeId: string;
  }) => string;
};

export const createEdgeSplitQueue = (args: {
  nodes: NodeMap;
  graph: Graph;
  epsilon: number;
}): EdgeSplitQueue => {
  const { nodes, graph, epsilon } = args;
  const edgeSplits = new Map<string, EdgeSplitGroup>();

  const getEdgeKey = (nodeAId: string, nodeBId: string): string => {
    return nodeAId < nodeBId
      ? `${nodeAId}|${nodeBId}`
      : `${nodeBId}|${nodeAId}`;
  };

  const queueEdgeSplit = (queueArgs: {
    nodeAId: string;
    nodeBId: string;
    splitPoint: { x: number; y: number };
    virtualNodeId: string;
  }): string => {
    const { nodeAId, nodeBId, splitPoint, virtualNodeId } = queueArgs;
    const nodeA = nodes[nodeAId];
    const nodeB = nodes[nodeBId];

    if (!nodeA || !nodeB) {
      return virtualNodeId;
    }

    const distToA = getEuclideanDistance({ pointA: splitPoint, pointB: nodeA });
    const distToB = getEuclideanDistance({ pointA: splitPoint, pointB: nodeB });

    if (distToA < epsilon) {
      return nodeAId;
    }

    if (distToB < epsilon) {
      return nodeBId;
    }

    nodes[virtualNodeId] = splitPoint;
    initializeVertex({ graph, nodeId: virtualNodeId });

    const edgeKey = getEdgeKey(nodeAId, nodeBId);
    const group = edgeSplits.get(edgeKey) ?? { nodeAId, nodeBId, splits: [] };
    group.splits.push({ virtualNodeId, splitPoint });
    edgeSplits.set(edgeKey, group);

    return virtualNodeId;
  };

  return { edgeSplits, getEdgeKey, queueEdgeSplit };
};
